import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CHAPTERS, CATEGORIES, PROFILE_IMAGE } from "@/data/content";

const QUOTE =
  "Mehr als nur Bilder — es sind Erinnerungen. Das ist nicht nur mein Slogan, sondern mein Versprechen bei jedem einzelnen Shooting.";

const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.12, 1]);
  const y = useTransform(progress, range, [8, 0]);
  return (
    <motion.span style={{ opacity, y }} className="inline-block">
      {children}&nbsp;
    </motion.span>
  );
};

const ScrollRevealQuote = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.45"] });
  const words = QUOTE.split(" ");
  return (
    <blockquote
      ref={ref}
      className="border-l-2 border-gold pl-6 font-serif text-2xl italic leading-relaxed text-zinc-200 lg:text-3xl"
      data-testid="manifesto-quote-reveal"
    >
      „{words.map((w, i) => (
        <Word key={i} progress={scrollYProgress} range={[i / words.length, Math.min(1, (i + 1.5) / words.length)]}>
          {w}
        </Word>
      ))}“
    </blockquote>
  );
};

export const Manifesto = () => {
  return (
    <section id="ueber-mich" data-testid="manifesto-section" className="border-t border-white/10 bg-ink2">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-6 py-28 lg:grid-cols-12 lg:px-12 lg:py-40">
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
          >
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.35em] text-gold">Über mich — Leno Fotografie</p>
            <h2 className="font-serif text-3xl font-light tracking-tight text-white sm:text-4xl lg:text-5xl">
              Drei Werte, <span className="italic text-zinc-400">ein Anspruch.</span>
            </h2>
          </motion.div>

          <div className="mt-12 space-y-0">
            {CHAPTERS.map((ch, i) => (
              <motion.div
                key={ch.nr}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="group border-t border-white/10 py-7 transition-colors last:border-b hover:bg-white/[0.02]"
                data-testid={`manifesto-chapter-${ch.nr}`}
              >
                <div className="flex items-baseline gap-5">
                  <span className="font-mono text-xs text-gold">{ch.nr}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-white transition-colors group-hover:text-gold">{ch.title}</h3>
                    <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-400">{ch.text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="relative lg:col-span-7 lg:pl-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1 }}
            className="relative"
          >
            <div className="relative ml-auto aspect-[4/3] w-full overflow-hidden border border-white/10 bg-black lg:w-[85%]">
              <img src="/assets/brand-card.png" alt="Leno Fotografie — Branding" data-testid="manifesto-studio-image" className="h-full w-full object-contain" />
            </div>
            <div className="absolute -bottom-10 left-0 w-48 overflow-hidden border border-white/10 lg:-left-4 lg:w-60">
              <img src={PROFILE_IMAGE} alt="Portrait von Leno" data-testid="manifesto-profile-image" className="aspect-[3/4] w-full object-cover" />
              <div className="bg-ink3 px-4 py-3">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold">Eric Leno</p>
                <p className="text-[11px] text-zinc-500">Chef, Fotograf & Geschichtensammler</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-20 lg:ml-auto lg:w-[85%]"
          >
            <ScrollRevealQuote />
            <div className="mt-10" data-testid="manifesto-bio">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Hinter der Kamera</p>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-300">
                Hallo, ich bin Eric. Ich bin 17 Jahre alt und Fotograf. Ich mache das als Hobby
                und ich bin kein Profi — aber ich liebe es, Momente festzuhalten, und gebe bei
                jedem Shooting mein Bestes.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              {CATEGORIES.filter((c) => c.key !== "alle").map((c) => (
                <span
                  key={c.key}
                  className="rounded-full border border-white/10 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400"
                >
                  {c.label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
